import {
    line,
    curveBasis,
    curveLinear,
    curveCardinal,
    CurveCardinalFactory,
    CurveFactory,
    scaleLinear,
} from 'd3'
import { BinData, CurveOptions, ScaleType } from './simple-chart.ts'

type ScaleLinear = d3.ScaleLinear<number, number, never>

const DEFAULT_COLOR = '#399098'

const curvesTypes = { curveBasis, curveLinear, curveCardinal }

const TRANSITION_TIME = 378
const UPDATE_TRANSITION = 'curve-update'
const EMPHASIS_TRANSITION = 'curve-emphasis'

// Should be settable?
const FILL_OPACITY = 0.25

const HALF_FILL_OPACITY = (2 - Math.sqrt(4 - 4 * FILL_OPACITY)) / 2

type Prop = 'n' | 'n_pct'

export default class SimpleCurve {
    curveType: 'curveBasis' | 'curveLinear' | 'curveCardinal' = 'curveBasis'

    curveFn: CurveCardinalFactory | CurveFactory = curveBasis

    g: d3.Selection<SVGGElement, unknown, null, undefined>

    data: BinData[]

    curve: d3.Selection<SVGPathElement, unknown, null, undefined>

    strokeOnly: boolean = false

    strokeWidth: number = 3

    split: boolean = false

    xScale: ScaleLinear = scaleLinear()

    yScale: ScaleLinear = scaleLinear()

    prop: ScaleType = 'n_pct'

    color: string = '#ccc'

    constructor(
        container: d3.Selection<SVGGElement, unknown, null, undefined>,
        data: BinData[],
        xScale: ScaleLinear,
        yScale: ScaleLinear,
        prop: Prop,
        options: CurveOptions
    ) {
        this.g = container.append('g').classed('simple-curve', true)

        this.data = data
        this.setOptions(options)
        this.setCurveType(options.curveType || this.curveType)

        this.curve = this.g
            .append('path')
            .attr('fill-opacity', FILL_OPACITY)
            .attr('fill', this.color)
            .attr('stroke', this.color)

        this.updateCurve(data, xScale, yScale, prop, options, false)
    }

    setOptions(options: CurveOptions) {
        this.color = options.color || DEFAULT_COLOR

        this.strokeOnly = Boolean(options.strokeOnly)
        const defaultStrokeWidth = this.strokeOnly ? 3 : 2
        this.strokeWidth = options.strokeWidth
            ? Number(options.strokeWidth)
            : defaultStrokeWidth
        this.split = Boolean(options.split)
    }

    async updateCurve(
        data: BinData[],
        xScale: ScaleLinear,
        yScale: ScaleLinear,
        prop: Prop,
        options: CurveOptions,
        isTransition = true
    ) {
        this.setOptions(options)
        if (this.data.length > data.length) {
            while (data.length < this.data.length) {
                data.unshift(data[0])
            }
        } else if (this.data.length < data.length) {
            while (this.data.length < data.length) {
                this.data.unshift(this.data[0])
            }
            // Set up the same number of points before possibly doing a transition
            await this.update(false)
        }

        this.data = data
        this.xScale = xScale
        this.yScale = yScale
        this.prop = prop

        return this.update(isTransition)
    }

    zeroCurve(duration = TRANSITION_TIME) {
        const lineFn = line<BinData>()
            .curve(this.curveFn)
            .x((d) => this.xScale(d.bin))
            .y(() => this.yScale(0))

        return this.curve
            .transition(UPDATE_TRANSITION)
            .duration(duration)
            .attr('d', lineFn(this.data))
            .end()
    }

    update(isTransition = true) {
        const duration = isTransition ? TRANSITION_TIME : 0
        const filteredData = this.data.filter((d) => !d.fake)
        this.g
            .selectAll<SVGCircleElement, BinData>('circle.point')
            .data(filteredData, (d) => d.bin)
            .join(
                (enter) =>
                    enter
                        .append('circle')
                        .classed('point', true)
                        .attr('fill', this.color)
                        .attr('cx', (d) => this.xScale(d.bin))
                        .attr('cy', (d) => this.yScale(d[this.prop]))
                        .attr('opacity', 0.2)
                        .attr('r', 3),
                (update) => {
                    update
                        .transition(UPDATE_TRANSITION)
                        .duration(duration)
                        .attr('fill', this.color)
                        .attr('cx', (d) => this.xScale(d.bin))
                        .attr('cy', (d) => this.yScale(d[this.prop]))
                    return update
                }
            )

        // This is probably overkill, but I'm having fun
        // this.zeroCurve()

        const lineFn = line<BinData>()
            .curve(this.curveFn)
            .x((d) => this.xScale(d.bin))
            .y((d) => this.yScale(d[this.prop]))

        const fillColor = this.strokeOnly ? 'none' : this.color

        return (
            this.curve
                .transition(UPDATE_TRANSITION)
                .duration(duration)
                .attr('stroke-width', this.strokeWidth)
                .attr(
                    'fill-opacity',
                    this.split ? HALF_FILL_OPACITY : FILL_OPACITY
                )
                .attr('d', lineFn(this.data))
                .attr('stroke', this.color)
                .attr('fill', fillColor)
                .attr('stroke-dasharray', this.strokeOnly ? '4 4' : null)
                .end()
                // Swallow interrupt errors
                .catch(() => {})
        )
    }

    setCurveType(curveType: 'curveBasis' | 'curveLinear' | 'curveCardinal') {
        this.curveType = curveType
        this.curveFn = curvesTypes[curveType]
    }

    highlight(duration = TRANSITION_TIME) {
        this.g.attr('opacity', 1)
        return (
            this.curve
                .transition(EMPHASIS_TRANSITION)
                .duration(duration)
                .attr('fill-opacity', FILL_OPACITY * 3)
                .end()
                // Swallow interrupt errors
                .catch(() => true)
        )
    }

    removeHighlight(duration = TRANSITION_TIME) {
        this.g.attr('opacity', 1)
        return (
            this.curve
                .transition(EMPHASIS_TRANSITION)
                .duration(duration)
                .attr('fill-opacity', FILL_OPACITY)
                .end()
                // Swallow interrupt errors
                .catch(() => true)
        )
    }

    deemphasize(duration = TRANSITION_TIME) {
        this.g.attr('opacity', 0.3)
        return (
            this.curve
                .transition(EMPHASIS_TRANSITION)
                .duration(duration)
                .attr('fill-opacity', FILL_OPACITY)
                .end()
                // Swallow interrupt errors
                .catch(() => true)
        )
    }
}
